import React, { useRef, useState } from 'react';
import SwiperCore, { EffectFade } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import Eyebrow from './Eyebrow';
import useAutoplay from '../hooks/useAutoplay';
import { USE_WP_CHROME } from '../GlobalValues';

SwiperCore.use([EffectFade]);

/** Autoplay du diaporama collection — 5000 ms, valeur de la maquette. */
const AUTOPLAY_DELAY = 5000;

/** Durée du fondu — reprend le JS de la maquette (`@keyframes diapFade`, 180 ms). */
const FADE_SPEED = 180;

const pad = (value) => String(value).padStart(2, '0');

/**
 * `.diaporama` — diaporama de la collection.
 *
 * Swiper (déjà une dépendance du repo, utilisée par les richfields `common` et
 * par 2025_VOG) porte désormais le geste tactile — la version précédente
 * n'avait aucun handler de swipe. `effect="fade"` à 180 ms reprend la durée du
 * `@keyframes diapFade` de la maquette ; flèches/points/compteur restent nos
 * propres éléments, pilotés via l'instance Swiper plutôt que par les widgets
 * par défaut, pour ne rien changer au rendu déjà validé.
 *
 * Différence assumée avec la version précédente : les 6 diapositives sont
 * désormais toutes montées (Swiper a besoin des slides en DOM pour le geste
 * tactile), alors que l'ancienne version n'en rendait qu'une à la fois. Le
 * rendu de chaque diapositive au repos est inchangé.
 *
 * Corrections conservées :
 *  - piège n°7 : le total est dérivé de `slides.length`, jamais écrit en dur ;
 *  - décision 5 : le titre de chaque diapositive est rendu par React, donc
 *    présent dans le HTML servi (pas de réécriture JS invisible du crawler).
 */
const CollectionSlider = ({ eyebrow, slides = [] }) => {
  const [current, setCurrent] = useState(0);
  const containerRef = useRef(null);
  const swiperRef = useRef(null);
  const total = slides.length;

  const goTo = (index) => swiperRef.current && swiperRef.current.slideToLoop(((index % total) + total) % total);
  useAutoplay(() => swiperRef.current && swiperRef.current.slideNext(), AUTOPLAY_DELAY, containerRef, total > 1);

  if (!total) return null;

  return (
    <section
      // Retour client : la bordure du dessous doublonne avec celle du widget
      // `<wp-footer>` (`.site-rass`) qui suit immédiatement quand le chrome
      // WordPress est actif — chacune fait ~2px, côte à côte ça se voit comme
      // un trait deux fois trop épais. On la retire ici puisque c'est le
      // widget WP qui referme la séparation dans ce mode (`USE_WP_CHROME`).
      className={['diaporama', USE_WP_CHROME ? 'diaporama-no-bottom-border' : null].filter(Boolean).join(' ')}
      id="diaporama"
      ref={containerRef}
    >
      <div className="diap-wrap">
        <Swiper
          effect="fade"
          fadeEffect={{ crossFade: true }}
          speed={FADE_SPEED}
          loop={total > 1}
          allowTouchMove={total > 1}
          autoHeight
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          onSlideChange={(swiper) => setCurrent(swiper.realIndex)}
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={slide.src}>
              <div className="diap-slide">
                <div className="diap-content">
                  {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
                  <h2>
                    {slide.title.line1}
                    {slide.title.highlight && (
                      <>
                        <br />
                        <span className="hl">{slide.title.highlight}</span>
                      </>
                    )}
                  </h2>
                  <p>{slide.text}</p>
                </div>
                <div className="diap-photo">
                  <img
                    src={slide.src}
                    alt={slide.alt || `${slide.title.line1} ${slide.title.highlight || ''} — collection Tchip`.trim()}
                    width="520"
                    height="693"
                    loading={index === 0 ? 'eager' : 'lazy'}
                    fetchpriority={index === 0 ? 'high' : undefined}
                  />
                  <div className="photo-tag">
                    {pad(index + 1)} / {pad(total)}
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {total > 1 && (
          <div className="diap-controls">
            <div className="diap-arrows">
              <button type="button" onClick={() => goTo(current - 1)} aria-label="Précédent">
                ←
              </button>
              <button type="button" onClick={() => goTo(current + 1)} aria-label="Suivant">
                →
              </button>
            </div>
            <div className="diap-dots">
              {slides.map((item, index) => (
                <button
                  key={item.src}
                  type="button"
                  className={index === current ? 'active' : undefined}
                  aria-label={`Diapositive ${index + 1}`}
                  aria-current={index === current ? 'true' : undefined}
                  onClick={() => goTo(index)}
                />
              ))}
            </div>
            <div className="diap-counter" aria-live="polite">
              <span className="cur">{pad(current + 1)}</span> / {pad(total)}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CollectionSlider;
